import React, { useEffect, useMemo, useState } from "react";
import { Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { useTheme } from "@mui/material";

/**
 * LocationMarker component for showing the delivery center location
 * Optimized for performance with SVG-based marker and proper memoization
 */
export const LocationMarker = React.memo(({ centerLat, centerLng }) => {
  const map = useMap();
  const theme = useTheme();
  const [pulse, setPulse] = useState(true);

  // Create a custom SVG marker that's more efficient than rendering React components
  const icon = useMemo(() => {
    // Primary color with pulse animation effect
    const primaryColor = theme?.palette?.primary?.main || "#ef1212";
    const secondaryColor = theme?.palette?.primary?.dark || "#c50000";

    // Create an SVG string directly rather than rendering a React component
    // This is more efficient for Leaflet rendering
    const svgString = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="40" height="40">
        <defs>
          <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="1" dy="1" stdDeviation="1" flood-opacity="0.3" />
          </filter>
          ${
            pulse
              ? `
          <radialGradient id="pulse" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
            <stop offset="0%" stop-color="${primaryColor}" stop-opacity="0.3">
              <animate attributeName="stop-opacity" values="0.3;0.7;0.3" dur="2s" repeatCount="indefinite" />
            </stop>
            <stop offset="100%" stop-color="${primaryColor}" stop-opacity="0">
              <animate attributeName="stop-opacity" values="0;0.3;0" dur="2s" repeatCount="indefinite" />
            </stop>
          </radialGradient>
          `
              : ""
          }
        </defs>
        ${pulse ? `<circle cx="12" cy="12" r="12" fill="url(#pulse)" />` : ""}
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" 
          fill="${primaryColor}" stroke="${secondaryColor}" stroke-width="0.5" filter="url(#shadow)" />
        <circle cx="12" cy="9" r="2.5" fill="white" />
      </svg>
    `;

    return L.divIcon({
      className: "delivery-center-marker",
      html: svgString,
      iconSize: [40, 40],
      iconAnchor: [20, 40], // Center bottom of the icon
      popupAnchor: [0, -40], // Center top of the icon
    });
  }, [theme, pulse]);

  // Effect to set up map and enable pulsing effect on first load
  useEffect(() => {
    // Only try to locate user on initial render
    map.locate();

    // Animate marker pulse on load
    const togglePulse = () => {
      setPulse((prevPulse) => !prevPulse);
      setTimeout(togglePulse, 5000); // Toggle every 5 seconds
    };

    // Delayed start of animation to ensure smooth initial loading
    const timer = setTimeout(togglePulse, 5000);

    return () => clearTimeout(timer);
  }, [map]); // Only re-run if map object changes

  // If no coordinates, return nothing
  if (!centerLat || !centerLng) return null;

  return (
    <Marker
      position={[centerLat, centerLng]}
      icon={icon}
      zIndexOffset={1000} // Ensure this marker is above others
    >
      <Popup className="delivery-center-popup">
        <div style={{ padding: "5px", textAlign: "center" }}>
          <strong>Central de Entregas</strong>
        </div>
      </Popup>
    </Marker>
  );
});

// Display name for debugging
LocationMarker.displayName = "LocationMarker";
